import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generates a PDF from a DOM element or a list of page elements.
 * @param {string|string[]} elementIds - The ID(s) of the DOM element(s) to capture.
 * @param {string} fileName - The name of the downloaded PDF file.
 * @returns {Promise<boolean>} - Resolves to true if successful.
 */
export const generatePDF = async (elementIds, fileName = 'analytics-report.pdf') => {
  const ids = Array.isArray(elementIds) ? elementIds : [elementIds];
  
  if (ids.length === 0) return false;

  const pdf = new jsPDF({
    orientation: 'portrait', // Default, will change per page
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  try {
    for (let i = 0; i < ids.length; i++) {
      const element = document.getElementById(ids[i]);
      if (!element) {
        console.warn(`Element with id "${ids[i]}" not found`);
        continue;
      }

      // Check orientation preference from class
      const isLandscape = element.classList.contains('landscape');
      const orientation = isLandscape ? 'landscape' : 'portrait';

      const canvas = await html2canvas(element, {
        scale: 2, // Reduced from 2 to 1.5 to save size while maintaining decent quality
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });

      // Use JPEG with 0.8 quality instead of PNG for significant size reduction
      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      
      // If not the first page, add a new page
      if (i > 0) {
        pdf.addPage('a4', orientation);
      } else {
        // Set first page orientation
        if (isLandscape) {
           pdf.deletePage(1);
           pdf.addPage('a4', 'landscape');
        }
      }

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Use full page (0 margins) because the component design handles padding
      // Use FAST compression for better performance
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
    }

    pdf.save(fileName);
    return true;
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw error;
  }
};
